function initCountdowns(root) {
  const cartDrawer = root.closest('cart-drawer') || root;
  const isDesignMode = typeof Shopify !== 'undefined' && Shopify.designMode;

  if (cartDrawer?.classList?.contains('is-empty') && !isDesignMode) {
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith('tsl-cart-countdown-end')) localStorage.removeItem(key);
    });
  }

  root.querySelectorAll('.cart-drawer-countdown').forEach((element) => {
    if (element._countdownInterval) {
      clearInterval(element._countdownInterval);
      delete element._countdownInterval;
    }

    const mode = element.dataset.mode || 'minutes';
    const minutes = parseInt(element.dataset.minutes, 10) || 15;
    const storageKey = `tsl-cart-countdown-end-${minutes}`;
    const timerElement = element.querySelector('.cart-drawer-countdown__timer');
    const inlineElement = element.querySelector(
      '.cart-drawer-countdown__inline, .cart-drawer-countdown__message'
    );
    const finishedElement = element.querySelector('.cart-drawer-countdown__finished');
    const deadlineTs = parseInt(element.dataset.end, 10);
    const isDeadline = mode === 'deadline';

    const setFinishedState = () => {
      if (inlineElement) inlineElement.hidden = true;
      if (finishedElement) {
        const finishedText = finishedElement.dataset.finishedText || finishedElement.textContent || '';
        finishedElement.textContent = finishedText;
        finishedElement.hidden = false;
        finishedElement.setAttribute('aria-live', 'polite');
      }
      if (timerElement) timerElement.textContent = '0:00';
    };

    const setActiveState = () => {
      if (inlineElement) inlineElement.hidden = false;
      if (finishedElement) {
        finishedElement.hidden = true;
        finishedElement.removeAttribute('aria-live');
      }
    };

    if (element.dataset.invalid === 'true' && !isDesignMode) {
      setFinishedState();
      return;
    }

    let endTime;
    const now = Date.now();

    if (isDeadline && deadlineTs > 0) {
      endTime = deadlineTs * 1000;
      if (isDesignMode && endTime <= now) {
        endTime = now + 15 * 60 * 1000;
      }
    } else if (isDeadline && !deadlineTs) {
      if (isDesignMode) {
        endTime = now + 15 * 60 * 1000;
      } else {
        setFinishedState();
        return;
      }
    } else if (isDesignMode) {
      endTime = now + minutes * 60 * 1000;
    } else {
      endTime = parseInt(localStorage.getItem(storageKey), 10);
      if (!endTime || endTime <= now) {
        endTime = now + minutes * 60 * 1000;
        localStorage.setItem(storageKey, endTime.toString());
      }
    }

    const tick = () => {
      const remainingSeconds = Math.max(0, Math.ceil((endTime - Date.now()) / 1000));

      if (remainingSeconds <= 0) {
        setFinishedState();
        if (element._countdownInterval) {
          clearInterval(element._countdownInterval);
          delete element._countdownInterval;
        }
        return;
      }

      setActiveState();
      if (timerElement) {
        const hours = Math.floor(remainingSeconds / 3600);
        const mins = Math.floor((remainingSeconds % 3600) / 60);
        const secs = remainingSeconds % 60;
        if (hours >= 24) {
          const days = Math.floor(hours / 24);
          timerElement.textContent = `${days}d ${String(hours % 24).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
        } else if (hours > 0) {
          timerElement.textContent = `${hours}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
        } else {
          timerElement.textContent = `${mins}:${String(secs).padStart(2, '0')}`;
        }
      }
    };

    const startInterval = () => {
      if (element._countdownInterval) return;
      tick();
      if (Math.max(0, Math.ceil((endTime - Date.now()) / 1000)) > 0) {
        element._countdownInterval = setInterval(tick, 1000);
      }
    };

    const pauseInterval = () => {
      if (element._countdownInterval) {
        clearInterval(element._countdownInterval);
        delete element._countdownInterval;
      }
    };

    element._countdownTick = tick;
    element._countdownStart = startInterval;
    element._countdownPause = pauseInterval;

    const drawerIsOpen =
      cartDrawer?.classList?.contains('active') ||
      cartDrawer?.querySelector?.('#CartDrawer')?.hasAttribute('open') ||
      isDesignMode;

    if (drawerIsOpen) {
      startInterval();
    } else {
      tick();
      pauseInterval();
    }
  });
}

function syncCountdownWithDrawer(cartDrawer) {
  if (!cartDrawer) return;
  const isOpen =
    cartDrawer.classList.contains('active') ||
    cartDrawer.querySelector('#CartDrawer')?.hasAttribute('open');

  cartDrawer.querySelectorAll('.cart-drawer-countdown').forEach((element) => {
    if (isOpen) {
      element._countdownStart?.();
    } else {
      element._countdownPause?.();
    }
  });
}

function initUpsells(root, abortController) {
  root.querySelectorAll('.cart-drawer-upsell__button').forEach((button) => {
    button.addEventListener(
      'click',
      async () => {
        const variantId = button.dataset.variantId;
        if (!variantId || button.disabled) return;

        const cartDrawer = document.querySelector('cart-drawer');
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', 1);

        if (cartDrawer) {
          formData.append(
            'sections',
            cartDrawer.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
        }

        config.body = formData;

        try {
          const response = await fetch(`${routes.cart_add_url}`, config).then((res) => res.json());

          if (response.status) {
            console.error(response.description || response.message);
            return;
          }

          if (cartDrawer?.classList.contains('is-empty')) {
            cartDrawer.classList.remove('is-empty');
          }

          await publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'cart-drawer-premium',
            cartData: response,
            variantId,
          });

          if (cartDrawer) {
            cartDrawer.renderContents(response);
          }
        } catch (error) {
          console.error(error);
        } finally {
          button.disabled = false;
          button.removeAttribute('aria-busy');
        }
      },
      { signal: abortController.signal }
    );
  });
}

function initFreeShippingBars() {
  // Progress is rendered server-side; reserved for future dynamic updates.
}

function setDiscountStatus(root, message, isError = false) {
  const status = root.querySelector('[data-cart-drawer-discount-status]');
  if (!status) return;

  if (!message) {
    status.hidden = true;
    status.textContent = '';
    status.classList.remove('is-error', 'is-success');
    return;
  }

  status.hidden = false;
  status.textContent = message;
  status.classList.toggle('is-error', isError);
  status.classList.toggle('is-success', !isError);
}

function getAppliedDiscountCodes(cart) {
  const codes = new Set();

  (cart?.discount_codes || []).forEach((entry) => {
    const code = typeof entry === 'string' ? entry : entry?.code;
    if (code) codes.add(String(code));
  });

  if (codes.size > 0) return Array.from(codes);

  (cart?.cart_level_discount_applications || []).forEach((discount) => {
    if (discount.title) codes.add(discount.title);
  });

  (cart?.items || []).forEach((item) => {
    (item.discounts || []).forEach((discount) => {
      if (discount.title) codes.add(discount.title);
    });
  });

  return Array.from(codes);
}

async function applyCartDiscount(code) {
  const response = await fetch(`${routes.cart_update_url || `${window.Shopify?.routes?.root || '/'}cart/update.js`}`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ discount: code }),
  });

  if (!response.ok) {
    throw new Error('discount_update_failed');
  }

  return response.json();
}

async function refreshCartDrawerAfterDiscount(cartDrawer, cartData) {
  if (!cartDrawer) return;

  const sections = cartDrawer.getSectionsToRender?.().map((section) => section.id) || ['cart-drawer', 'cart-icon-bubble'];
  const sectionResponse = await fetch(`${routes.cart_url}?sections=${sections.join(',')}`);
  const sectionsJson = await sectionResponse.json();

  const payload = {
    ...cartData,
    sections: sectionsJson,
  };

  if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    await publish(PUB_SUB_EVENTS.cartUpdate, {
      source: 'cart-drawer-discount',
      cartData: payload,
    });
  }

  cartDrawer.renderContents?.(payload);
  initCartDrawerPremium(cartDrawer);
}

function initDiscountForms(root, abortController) {
  root.querySelectorAll('[data-cart-drawer-discount]').forEach((discountRoot) => {
    const form = discountRoot.querySelector('[data-cart-drawer-discount-form]');
    if (!form) return;

    const input = form.querySelector('[data-cart-drawer-discount-input]');
    const submit = form.querySelector('[data-cart-drawer-discount-submit]');
    const strings = window.tslCartDiscountStrings || {};

    form.addEventListener(
      'submit',
      async (event) => {
        event.preventDefault();
        const code = (input?.value || '').trim();
        if (!code) {
          setDiscountStatus(discountRoot, strings.empty || 'Enter a discount code', true);
          return;
        }

        const cartDrawer = document.querySelector('cart-drawer');
        submit.disabled = true;
        submit.setAttribute('aria-busy', 'true');
        submit.classList.add('is-loading');
        const submitLabel = submit.querySelector('.cart-drawer-discount__submit-label');
        const submitLoading = submit.querySelector('.cart-drawer-discount__submit-loading');
        if (submitLabel) submitLabel.hidden = true;
        if (submitLoading) submitLoading.hidden = false;
        setDiscountStatus(discountRoot, '');

        try {
          const existingCart = await fetch(`${routes.cart_url}.js`).then((res) => res.json());
          const existingCodes = getAppliedDiscountCodes(existingCart);
          const nextCodes = Array.from(new Set([...existingCodes, code]));
          const updatedCart = await applyCartDiscount(nextCodes.join(','));
          const discountCodes = updatedCart.discount_codes || [];
          const codeMatched = discountCodes.some((entry) => {
            const value = (entry.code || entry || '').toString().toLowerCase();
            return value === code.toLowerCase() && entry.applicable !== false;
          });
          const savingsIncreased = (updatedCart.total_discount || 0) > (existingCart.total_discount || 0);
          const appsIncreased =
            (updatedCart.cart_level_discount_applications || []).length >
            (existingCart.cart_level_discount_applications || []).length;

          if (!codeMatched && !savingsIncreased && !appsIncreased) {
            setDiscountStatus(discountRoot, strings.invalid || 'Enter a valid discount code', true);
            return;
          }

          input.value = '';
          setDiscountStatus(discountRoot, strings.success || 'Discount applied', false);
          await refreshCartDrawerAfterDiscount(cartDrawer, updatedCart);
        } catch (error) {
          console.error(error);
          setDiscountStatus(discountRoot, strings.error || 'Could not apply discount code', true);
        } finally {
          submit.disabled = false;
          submit.removeAttribute('aria-busy');
          submit.classList.remove('is-loading');
          if (submitLabel) submitLabel.hidden = false;
          if (submitLoading) submitLoading.hidden = true;
        }
      },
      { signal: abortController.signal }
    );

    discountRoot.querySelectorAll('[data-cart-drawer-discount-remove]').forEach((button) => {
      button.addEventListener(
        'click',
        async () => {
          const cartDrawer = document.querySelector('cart-drawer');
          const codeToRemove = button.dataset.discountCode;
          button.disabled = true;
          button.setAttribute('aria-busy', 'true');
          button.classList.add('is-loading');
          const removeLoading = button.querySelector('.cart-drawer-discount__remove-loading');
          if (removeLoading) removeLoading.hidden = false;

          try {
            const existingCart = await fetch(`${routes.cart_url}.js`).then((res) => res.json());
            const remaining = getAppliedDiscountCodes(existingCart).filter(
              (code) => code.toLowerCase() !== (codeToRemove || '').toLowerCase()
            );
            const updatedCart = await applyCartDiscount(remaining.join(','));
            await refreshCartDrawerAfterDiscount(cartDrawer, updatedCart);
          } catch (error) {
            console.error(error);
            setDiscountStatus(discountRoot, strings.error || 'Could not remove discount code', true);
          } finally {
            button.disabled = false;
            button.removeAttribute('aria-busy');
            button.classList.remove('is-loading');
            if (removeLoading) removeLoading.hidden = true;
          }
        },
        { signal: abortController.signal }
      );
    });
  });
}

async function hydrateCartDrawerPremium() {
  const drawer = document.querySelector('cart-drawer');
  if (!drawer) return;

  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    initCartDrawerPremium(drawer);
    return;
  }

  const needsHydration =
    drawer.querySelector('[data-cart-drawer-hydrate="top"]') ||
    drawer.querySelector('[data-cart-drawer-hydrate="bottom"]');

  if (!needsHydration) {
    initCartDrawerPremium(drawer);
    return;
  }

  try {
    const response = await fetch(`${routes.cart_url}?section_id=cart-drawer`);
    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');

    const topSource = doc.querySelector('.cart-drawer-premium-top');
    const bottomSource = doc.querySelector('.cart-drawer-premium');
    const topTarget = drawer.querySelector('.cart-drawer-premium-top');
    const bottomTarget = drawer.querySelector('.cart-drawer-premium');

    if (topSource) {
      if (topTarget) {
        topTarget.replaceWith(topSource.cloneNode(true));
      } else {
        drawer.querySelector('.drawer__header')?.insertAdjacentElement('afterend', topSource.cloneNode(true));
      }
    }

    if (bottomSource) {
      if (bottomTarget) {
        bottomTarget.replaceWith(bottomSource.cloneNode(true));
      } else {
        drawer.querySelector('cart-drawer-items')?.insertAdjacentElement('afterend', bottomSource.cloneNode(true));
      }
    }

    initCartDrawerPremium(drawer);
  } catch (error) {
    console.error(error);
    initCartDrawerPremium(drawer);
  }
}

function getCartDrawerRoots(scope = document) {
  const roots = [];

  if (scope?.tagName === 'CART-DRAWER') {
    roots.push(scope);
  }

  if (typeof scope.querySelectorAll === 'function') {
    scope.querySelectorAll('cart-drawer').forEach((drawer) => roots.push(drawer));
  }

  if (!roots.length) {
    document.querySelectorAll('cart-drawer').forEach((drawer) => roots.push(drawer));
  }

  return roots;
}

function destroyCartDrawerPremium(root) {
  root.querySelectorAll('.cart-drawer-countdown').forEach((element) => {
    if (element._countdownInterval) {
      clearInterval(element._countdownInterval);
      delete element._countdownInterval;
    }
  });

  root.querySelectorAll('[data-cart-drawer-discount-form]').forEach((form) => {
    delete form.dataset.initialized;
  });

  if (root._cartDrawerPremiumAbortController) {
    root._cartDrawerPremiumAbortController.abort();
    delete root._cartDrawerPremiumAbortController;
  }
}

function initCartDrawerPremium(scope = document) {
  const roots = getCartDrawerRoots(scope);

  roots.forEach((root) => {
    const hasPremium =
      root.querySelector('.cart-drawer-premium') ||
      root.querySelector('.cart-drawer-premium-top') ||
      root.querySelector('.cart-drawer-countdown') ||
      root.querySelector('.cart-drawer-free-shipping');
    if (!hasPremium) return;

    destroyCartDrawerPremium(root);

    const abortController = new AbortController();
    root._cartDrawerPremiumAbortController = abortController;

    initCountdowns(root);
    initFreeShippingBars(root);
    initUpsells(root, abortController);
    initDiscountForms(root, abortController);

    if (!root._tslCountdownDrawerObserver) {
      const observer = new MutationObserver(() => syncCountdownWithDrawer(root));
      observer.observe(root, { attributes: true, attributeFilter: ['class'] });
      const details = root.querySelector('#CartDrawer');
      if (details) {
        observer.observe(details, { attributes: true, attributeFilter: ['open'] });
      }
      root._tslCountdownDrawerObserver = observer;
    }
    syncCountdownWithDrawer(root);
  });
}

function openCartDrawerForEditor() {
  if (typeof Shopify === 'undefined' || !Shopify.designMode) return;

  const drawer = document.querySelector('cart-drawer');
  if (!drawer) return;

  if (!drawer.classList.contains('active')) {
    drawer.open();
  }

  if (typeof window.initCartDrawerPremium === 'function') {
    window.initCartDrawerPremium(drawer);
  }
}

function isCartDrawerEditorEvent(event) {
  return (
    event.target?.classList?.contains('section-cart-drawer') ||
    event.target?.querySelector?.('cart-drawer') ||
    event.target?.closest?.('.section-cart-drawer')
  );
}

function isCartDrawerBlockEvent(event) {
  const target = event.target;
  if (!target) return false;

  return Boolean(
    target.closest?.('.section-cart-drawer') ||
      target.closest?.('cart-drawer') ||
      target.closest?.('.cart-drawer-premium') ||
      target.closest?.('.cart-drawer-premium-top') ||
      target.closest?.('.cart-drawer-upsell') ||
      target.closest?.('.cart-drawer-countdown') ||
      target.closest?.('.cart-drawer-free-shipping') ||
      target.closest?.('.cart-drawer-discount')
  );
}

function closeCartDrawerForEditor() {
  if (typeof Shopify === 'undefined' || !Shopify.designMode) return;

  const drawer = document.querySelector('cart-drawer');
  if (drawer?.classList?.contains('active') && typeof drawer.close === 'function') {
    drawer.close();
  }
}

window.initCartDrawerPremium = initCartDrawerPremium;
window.openCartDrawerForEditor = openCartDrawerForEditor;
window.hydrateCartDrawerPremium = hydrateCartDrawerPremium;

document.addEventListener('DOMContentLoaded', () => {
  if (document.querySelector('[data-cart-drawer-hydrate]')) {
    hydrateCartDrawerPremium();
  } else {
    initCartDrawerPremium();
  }
});

document.addEventListener('shopify:section:load', (event) => {
  if (isCartDrawerEditorEvent(event)) {
    initCartDrawerPremium(event.target);
    setTimeout(openCartDrawerForEditor, 50);
  }
});

document.addEventListener('shopify:section:select', (event) => {
  if (isCartDrawerEditorEvent(event)) {
    initCartDrawerPremium(event.target);
    openCartDrawerForEditor();
    return;
  }

  closeCartDrawerForEditor();
});

document.addEventListener('shopify:block:select', (event) => {
  if (!isCartDrawerBlockEvent(event)) {
    closeCartDrawerForEditor();
    return;
  }

  initCartDrawerPremium(event.target);
  openCartDrawerForEditor();
});

document.addEventListener('shopify:section:deselect', () => {
  closeCartDrawerForEditor();
});

document.addEventListener('shopify:section:unload', (event) => {
  const drawer = event.target?.querySelector?.('cart-drawer');
  if (drawer) destroyCartDrawerPremium(drawer);
});
